/**
 * Convert a little-endian buffer into a BigInt.
 * @param buf - The little-endian buffer to convert.
 * @returns A BigInt with the little-endian representation of buf.
 */
export function toBigIntLE(buf: Buffer): bigint {
    const reversed = Buffer.from(buf);
    reversed.reverse();
    const hex = reversed.toString('hex');
    if (hex.length === 0) {
      return BigInt(0);
    }
    return BigInt(`0x${hex}`);
  }
  
  /**
   * Convert a big-endian buffer into a BigInt.
   * @param buf - The big-endian buffer to convert.
   * @returns A BigInt with the big-endian representation of buf.
   */
  export function toBigIntBE(buf: Buffer): bigint {
    const hex = buf.toString('hex');
    if (hex.length === 0) {
      return BigInt(0);
    }
    return BigInt(`0x${hex}`);
  }
  
  /**
   * Convert a BigInt to a little-endian buffer.
   * @param num - The BigInt to convert.
   * @param width - The number of bytes that the resulting buffer should be.
   * @returns A little-endian buffer representation of num.
   */
  export function toBufferLE(num: bigint, width: number): Buffer {
    if (num < BigInt(0)) {
      throw new Error(`Cannot convert negative bigint ${num.toString()} to buffer with toBufferLE.`);
    }
    const hex = num.toString(16);
    const buffer = Buffer.from(hex.padStart(width * 2, '0').slice(0, width * 2), 'hex');
    buffer.reverse();
    return buffer;
  }
  
  /**
   * Convert a BigInt to a big-endian buffer.
   * @param num - The BigInt to convert.
   * @param width - The number of bytes that the resulting buffer should be.
   * @returns A big-endian buffer representation of num.
   */
  export function toBufferBE(num: bigint, width: number): Buffer {
    if (num < BigInt(0)) {
      throw new Error(`Cannot convert negative bigint ${num.toString()} to buffer with toBufferBE.`);
    }
    const hex = num.toString(16);
    const buffer = Buffer.from(hex.padStart(width * 2, '0').slice(0, width * 2), 'hex');
    if (buffer.length > width) {
      throw new Error(`Number ${num.toString(16)} does not fit in ${width}`);
    }
    return buffer;
  }
  
  /**
   * Converts a BigInt to its hex representation.
   * @param num - The BigInt to convert.
   * @param padTo32 - Whether to pad the resulting string to 32 bytes.
   * @returns An even-length 0x-prefixed string.
   */
  export function toHex(num: bigint, padTo32 = false): string {
    const str = num.toString(16);
    const targetLen = str.length % 2 === 0 ? str.length : str.length + 1;
    const paddedStr = str.padStart(padTo32 ? 64 : targetLen, '0');
    return `0x${paddedStr}`;
  }
  
  /**
   * Converts a hex string to a buffer. Throws if input is not a valid hex string.
   * @param value - The hex string to convert. May be 0x prefixed or not.
   * @returns A buffer.
   */
  export function fromHex(value: string): Buffer {
    const hexRegex = /^(0x)?[0-9a-fA-F]*$/;
    if (!hexRegex.test(value) || value.length % 2 !== 0) {
      throw new Error(`Invalid hex string: ${value}`);
    }
    return Buffer.from(value.replace(/^0x/i, ''), 'hex');
  }